import {
  addGlobalContexts,
  addPlugin,
  newTracker,
  setCustomUrl,
  setUserId,
  trackPageView,
  trackSelfDescribingEvent,
  trackStructEvent
} from '@snowplow/browser-tracker';
import {enableLinkClickTracking, LinkClickTrackingPlugin, trackLinkClick} from '@snowplow/browser-plugin-link-click-tracking';
import {TimezonePlugin} from '@snowplow/browser-plugin-timezone';
import {DebuggerPlugin} from '@snowplow/browser-plugin-debugger';
import {ConsentPlugin, enableGdprContext} from '@snowplow/browser-plugin-consent';
import {EngagementTracking} from "./EngagementTracking";
import {BrowserUtils} from "./BrowserUtils";
import {Logger} from "./Logger";
import {LimioPlugin} from "./LimioPlugin";
import {Datalayer} from "./Datalayer";
import {FallbackCookieTrackingPlugin} from "./FallbackCookieTrackingPlugin";
import {AdblockPlugin} from "./AdblockPlugin";

export function EntryPoint(_document, _window, _tracker_loader_script) {
  const _tracker_app_id = _tracker_loader_script.getAttribute("data-app") || "mediafin";
  const _tracker_env = _tracker_loader_script.getAttribute("data-env") || "production";
  const _datalayer_name = _tracker_loader_script.getAttribute("data-datalayer-name") || "trmfn_datalayer";
  let paywallReadyEvent = {};
  const _discoverRootDomain = _tracker_loader_script.getAttribute("data-discover-domain") === "true";
  let _tracker_cookie_domain = null;
  let _tracker_server = "tr2.mediafin.be";
  const _tracker_retryWaitTime = 500;
  //wait a bit by default, allow user cookies to be set
  let _tracker_fallbackWaitTime = 500;
  if(["mediafin","investor"].indexOf(_tracker_app_id) >= 0) {
    //the dataLayer should be active on these sites so wait for it
    _tracker_fallbackWaitTime = 2000;
  }
  const _anonymousTracking = _tracker_loader_script.getAttribute("data-anonymous-tracking") === "true";
  const didomiSnowplowVendorIds = ['c:mediafina-gb8N2YQr', 'c:mediafinv-LH4zQrU8']; //known vendor ids, the first one that exists of this array will be checked for consent
  let didomiIntegrationEnabled = _tracker_loader_script.getAttribute("data-didomi-integration-enabled") || "true";
  let _debugEnabled = _tracker_loader_script.getAttribute("data-debug-enabled") === "true" || window.location.search.indexOf("debug_tracking=true") > -1;

  if (["acceptance", "acc", "test", "tst"].indexOf(_tracker_env.toLowerCase()) >= 0) {
    _tracker_server = "tr.acceptance.mediafin.be";
    _debugEnabled = true;
  }
  if (["subscription", "multimedia"].indexOf(_tracker_app_id) >= 0) {
    didomiIntegrationEnabled = "false";
  }
  if (_discoverRootDomain === false) {
    //default behavior for existing sites where below logic was used, note the leading dot char
    _tracker_cookie_domain = "." + _window.location.hostname.split('.').splice(1).join('.');
  }
  if ("localhost" === _window.location.hostname) {
    _tracker_cookie_domain = "localhost";
  }
  let trackerInstance;
  let trackerInitialised = false;
  let logger = Logger(_debugEnabled);
  //Debug levels: none = 0, error = 1, warn = 2, debug = 3, info = 4
  const debugPlugin = DebuggerPlugin(_debugEnabled ? 3 : 1);
  const engagementTracking = EngagementTracking(_debugEnabled);
  const browserUtils = BrowserUtils(_debugEnabled);
  const sessionLessTrackingPlugin = FallbackCookieTrackingPlugin("uuid");
  const adBlockPlugin = new AdblockPlugin("https://pagead2.googlesyndication.com/tag/js/gpt.js");

  function setDebug(debug) {
    _debugEnabled = debug;
    engagementTracking.setDebug(debug);
    logger = Logger(_debugEnabled)
  }

  /*
  Cookies set by Accolad:
  •	userType -> user.segment.userType
  •	userCampaigns -> user.segment.campaignId
  •	userUuid -> user.profile.attributes.uuid
  •	userProfileId -> user.profile.profileInfo.profileId
   */
  function getTrackingUserId() {
    let userId;
    let userProfileId;
    try {
      //in event there is not urn prefix
      userProfileId = paywallReadyEvent.userProfileID;
      //there is urn prefix in cookie and window.digitalData
      const userProfileIdCookie = browserUtils.getCookieValue("userProfileId");
      if (!userProfileId && userProfileIdCookie && userProfileIdCookie.length > 0 && "Unknown" !== userProfileIdCookie) {
        userProfileId = userProfileIdCookie;
      }
      if (!userProfileId && window.digitalData && window.digitalData.user && window.digitalData.user.profile && window.digitalData.user.profile.profileInfo) {
        userProfileId = window.digitalData.user.profile.profileInfo.profileID;
      }
      if (userProfileId && userProfileId.indexOf("urn:user:") === 0) {
        userProfileId = userProfileId.replace("urn:user:", "");
      }
      userId = userProfileId;

      //TWIPE case
      if (typeof userId === "undefined") {
        const twipeCookie = browserUtils.getCookieValue("TW_LOGGED_IN");
        if (!!twipeCookie) {
          const decodedTwipeCookie = decodeURIComponent(twipeCookie);
          if (decodedTwipeCookie && decodedTwipeCookie.indexOf("subscriptionUserReference") > 0) {
            const subscriptionUserReference = JSON.parse(decodedTwipeCookie).subscriptionUserReference;
            userId = subscriptionUserReference.replace("urn:user:", "");
          }
        }
      }

      //Changemakers case
      if (typeof userId === "undefined" && "changemakers" === _tracker_app_id) {
        const userInLS = localStorage.getItem("user");
        if(userInLS && userInLS.length > 0 && Number(userInLS) > 0){
          userId = userInLS;
        }
      }
    } catch (e) {
      logger.error(e);
    }
    return userId;
  }

  function createUserContext() {
    if(isInitialised() === false) return undefined;
    logger.debug('createUserContext')
    const userContext = {schema: "iglu:be.mediafin.user/context/jsonschema/1-0-0", data: {}};
    const currentDomainUserId = trackerInstance.getDomainUserId(); //in case of cookies, can be empty string
    const fallbackUUID = sessionLessTrackingPlugin.getSessionLessID();
    if (currentDomainUserId && currentDomainUserId.length > 0) {
      userContext.data.uuid = "" + currentDomainUserId;
    } else if (fallbackUUID && fallbackUUID.length > 0) {
      userContext.data.uuid = "" + fallbackUUID;
    }
    const _userid = getTrackingUserId();
    if (!!_userid) userContext.data.userId = "" + _userid;

    const _userType = paywallReadyEvent.userType || browserUtils.getCookieValue("userType");
    switch (_userType) {
      case 'subscriber':
        userContext.data.usageType = "5";
        break;
      case 'visitor':
        userContext.data.usageType = "3";
        break;
      default:
        userContext.data.usageType = "1";
        break;
    }
    return userContext;
  }

  function createAssetContext() {
    const assetContext = {schema: "iglu:be.mediafin.asset/context/jsonschema/1-0-3", data: {}};
    if (typeof window.digitalData !== "undefined") {
      if (window.digitalData.page) {
        if (window.digitalData.page.category) {
          assetContext.data.pageCategory = window.digitalData.page.category.primaryCategory;
        }
        if (window.digitalData.page.pageInfo) {
          assetContext.data.assetId = window.digitalData.page.pageInfo.name;
          assetContext.data.creationDate = window.digitalData.page.pageInfo.creationDate;
          if (!assetContext.data.creationDate) assetContext.data.creationDate = browserUtils.getMetaTagContent('article:modified_time');
          assetContext.data.publishedDate = window.digitalData.page.pageInfo.publishedDate;
          if (!assetContext.data.publishedDate) assetContext.data.publishedDate = browserUtils.getMetaTagContent('article:published_time');
          assetContext.data.agency = window.digitalData.page.pageInfo.agency;
          if (window.digitalData.page.pageInfo.pageKey) {
            assetContext.data.pageKey = window.digitalData.page.pageInfo.pageKey.biKey;
          }
          if (window.digitalData.page.pageInfo.section) {
            assetContext.data.section = "" + window.digitalData.page.pageInfo.section;
          }
          if (window.digitalData.page.pageInfo.node) {
            assetContext.data.node = "" + window.digitalData.page.pageInfo.node;
          }
        }
        if (window.digitalData.page.attributes) {
          if (window.digitalData.page.attributes.assetType) {
            assetContext.data.assetType = "" + window.digitalData.page.attributes.assetType;
          } else if(window.digitalData.page.attributes.original_path && window.digitalData.page.attributes.original_path.indexOf('mme-articles') > 0){
            assetContext.data.assetType = "1";
          }
        }
        if (!!paywallReadyEvent.paywallActive) {
          const userId = getTrackingUserId();
          if (userId && userId.length > 0) {
            assetContext.data.accessType = "8"; //Y-bots
          } else {
            assetContext.data.accessType = "7";//X-bots
          }
        }
      }
    }
    const articleEl = document.querySelector('article div.c-articlecontents');
    if (articleEl && typeof articleEl.getBoundingClientRect === 'function') {
      const elRect = articleEl.getBoundingClientRect();
      assetContext.data.articleRectX = Math.round(elRect.x);
      assetContext.data.articleRectY = Math.round(elRect.y);
      assetContext.data.articleRectHeight = Math.round(elRect.height);
      assetContext.data.articleRectWidth = Math.round(elRect.width);
    }
    return assetContext;
  }

  function setupSnowplow() {
    if (didomiIntegrationEnabled === "false") {
      initTracker(true);
    } else {
      setupSnowplowDidomiListener(0);
    }
  }

  function getDatePrefix() {
    const now = new Date();
    const year = now.getFullYear().toString();
    const month = (now.getMonth() + 101).toString().substring(1);
    const day = (now.getDate() + 100).toString().substring(1);
    return year + month + day;
  }

  function initTracker(consentGiven) {
    logger.debug('trmfnloader: initTracker with consentGiven: ' + consentGiven + ', trackerInitialised: ' + trackerInitialised + ', anonymousTracking: ' + _anonymousTracking);
    if (trackerInitialised === false) {
      const isAnonymousTracking = (consentGiven === false || _anonymousTracking);
      trackerInstance = newTracker("trmfnv3", "https://" + _tracker_server, {
        appId: _tracker_app_id,
        cookieDomain: _tracker_cookie_domain,
        discoverRootDomain: _discoverRootDomain,
        anonymousTracking: isAnonymousTracking ? {withServerAnonymisation: true} : false,
        stateStorageStrategy: isAnonymousTracking ? "none" : "cookieAndLocalStorage",
        cookieLifetime: 60 * 60 * 24 * 365, //1 year (default is 2 years)
        eventMethod: 'post',
        postPath: '/be.mediafin/v2',
        contexts: {webPage: true},
        plugins: [debugPlugin, LinkClickTrackingPlugin(), TimezonePlugin(), ConsentPlugin()],
      });
      if ("subscription" === _tracker_app_id) {
        addPlugin({plugin: LimioPlugin()});
      }
      let linkClickDynamicContextArray = [];
      if ("mediafin" === _tracker_app_id) {//Tijd/Echo sites
        addPlugin({plugin: adBlockPlugin});
        if("/" === window.location.pathname){
          linkClickDynamicContextArray.push(createDynamicContextOnLinkClick);
        }
      }
      if (isAnonymousTracking) {
        addPlugin({plugin: sessionLessTrackingPlugin})
      } else {
        enableGdprContext({
          basisForProcessing: 'consent',
          documentId: 'consent-' + _tracker_app_id
        });
      }
      addGlobalContexts([createUserContext]);
      setUserId(getTrackingUserId());
      trackerInitialised = true;

      let originalUrl = _window.location.href;
      let customUrl = originalUrl;
      if (customUrl.indexOf('utm_campaign') > 0) {
        customUrl = browserUtils.removeEmptyUtmParameters(customUrl);
        const datePrefix = getDatePrefix() + "_";
        customUrl = customUrl.replace("utm_campaign=NIEUWOP", "utm_campaign=" + datePrefix + "NIEUWOP_NL");
        customUrl = customUrl.replace("utm_campaign=NOUVEAUSUR", "utm_campaign=" + datePrefix + "NOUVEAUSUR_FR");
      }
      if (originalUrl !== customUrl) {
        setCustomUrl(customUrl);
      }

      enableLinkClickTracking({pseudoClicks: true, trackContent: true, context: linkClickDynamicContextArray});
      engagementTracking.setupLinkClickTrackingRefresh(0);
      engagementTracking.setupButtonClickTrackingWithRefresh(0, 0);
      engagementTracking.setupPagePings();
      setTimeout(trackMediafinPageView, 500); //allow paywall to come up

      // SwiperJS is somehow blocking link click tracking from the plugin, fix here
      document.querySelectorAll('div.swiper-container a').forEach(anchor => {
        logger.debug("Add trackLinkClick to", anchor);
        anchor.addEventListener("click", function(event){
          const eventTarget = event.target;
          logger.debug("Link click event target", eventTarget);
          const contexts = [];
          contexts.push(createDynamicContextOnLinkClick(eventTarget));
          trackLinkClick({targetUrl: eventTarget.href, elementId: eventTarget.id, elementClasses: [...eventTarget.classList], elementContent: eventTarget.innerText, context: contexts});
        });
      })
    } else {
      logger.debug('trmfnloader: SP already initialized');
    }
  }

  function createDynamicContextOnLinkClick(element) {
    function createContextRecursive(element) {
      let context = {
        schema: "iglu:be.mediafin.asset/component_context/jsonschema/1-0-0",
        data: {}
      };
      if (element.hasAttribute('data-component-name')) {
        context.data.name = element.getAttribute('data-component-name');
        if (element.hasAttribute('data-id')) context.data.id = element.getAttribute('data-id');
        if (element.hasAttribute('data-type')) context.data.type = element.getAttribute('data-type');
        if (element.hasAttribute('data-title')) context.data.title = element.getAttribute('data-title');
        if (element.hasAttribute('data-source')) context.data.source = element.getAttribute('data-source');
        return context;
      } else if (element.className && element.className.indexOf('c-topArticles') >= 0) {
        context.data.name = "top-3-articles";
        return context;
      } else if (element.className && element.className.indexOf('theme-breaking') >= 0) {
        context.data.name = "breaking-news";
        return context;
      } else if (element.className && (element.className.indexOf('pagefeed-article-item') >= 0 || element.className.indexOf('c-articlelist-group') >= 0)) {
        context.data.name = "in-the-news-feed";
        return context;
      } else if (element.className && element.className.indexOf('c-recentnews__newslist') >= 0) {
        context.data.name = "recent-news-feed-top-right";
        return context;
      } else if (element.className
          && (element.className.indexOf('sub-grid-wide') >= 0 || element.className.indexOf('swiper-carousel') >= 0)
          && element.previousElementSibling.className.indexOf('segment-header') >= 0) {
        context.data.name = element.previousElementSibling.querySelector('h2').id;
        context.data.type = "sub-grid-wide";
        return context;
      } else if ("BODY" === element.tagName) {
        return undefined;
      }
      return createContextRecursive(element.parentElement);
    }

    return createContextRecursive(element);
  }

  function trackMediafinPageView(additionalContexts) {
    const assetContext = createAssetContext();
    const contexts = [];
    if (Object.getOwnPropertyNames(assetContext.data).length > 0) {
      contexts.push(assetContext);
    }
    if (Array.isArray(additionalContexts)) {
      Array.prototype.push.apply(contexts, additionalContexts)
    } else if (typeof additionalContexts === "object") {
      contexts.push(additionalContexts);
    }

    logger.debug('trackMediafinPageView', contexts);
    setUserId(getTrackingUserId());
    trackPageView({
        context: contexts // array of custom contexts
      }
    );
  }

  function setupSnowplowDidomiListener(attempt) {
    if (typeof Didomi !== 'undefined' && typeof Didomi.getVendorById === 'function' && typeof Didomi.getUserStatusForVendor === 'function') {
      let didomiSnowplowVendorId = didomiSnowplowVendorIds.filter(vendorId => typeof Didomi.getVendorById(vendorId) !== 'undefined').shift();
      logger.debug('trmfnloader: Didomi sp listener setup, didomiSnowplowVendorId', didomiSnowplowVendorId);
      const spConsentStatus = Didomi.getUserStatusForVendor(didomiSnowplowVendorId);
      logger.debug('trmfnloader: Didomi sp consent status', spConsentStatus);
      if (typeof spConsentStatus === 'undefined') {
        logger.debug('trmfnloader: consent screen still open, waiting for vendor:', didomiSnowplowVendorId, 'Overview of current consents:', Didomi.getUserConsentStatusForAll());
        setTimeout(setupSnowplowDidomiListener, 200, attempt + 1);
      } else {
        const consentGiven = spConsentStatus !== false;
        initTracker(consentGiven);
        if (spConsentStatus === false) {
          trackerInstance.clearUserData();
        }
      }
    } else if (attempt < 5) {
      setTimeout(setupSnowplowDidomiListener, 200, attempt + 1);
    } else if (document.cookie.indexOf("_sp_id.") >= 0) {
      //cookie is present so Snowplow has been approved before, insert tag immediately
      initTracker(true);
    } else {
      initTracker(false);
    }
  }

  function getSessionId() {
    if(isInitialised() === false) return undefined;
    //domain info docs: https://docs.snowplow.io/docs/collecting-data/collecting-from-own-applications/javascript-trackers/browser-tracker/browser-tracker-v3-reference/advanced-usage/tracker-information/
    const domainInfo = trackerInstance.getDomainUserInfo();
    const sessionLessID = sessionLessTrackingPlugin.getSessionLessID();
    logger.debug("getSessionId, domain info: ", domainInfo, ", sessionless ID: ", sessionLessID);
    if (typeof domainInfo === 'object' && Array.isArray(domainInfo) && domainInfo.length >= 7) {
      return domainInfo[6];
    }
    return sessionLessID;
  }

  /**
   * Below can be triggered with function call on iframe. Make sure the listener is registered on the page. Examples:
   * window.parent.postMessage({"tracker":"sp","action":"trackStructEvent","event":{"category":"multimedia-tools","action":"use-tool","label":"electric"}},"*")
   * window.parent.postMessage({"tracker":"sp","action":"trackSelfDescribingEvent","data":{"schema":"iglu:be.mediafin.multimedia/electric-vehicle/jsonschema/1-0-0","data":{"depart":"brussels","dest":"paris","vehicle":"tesla Y","conditions":"easy"}}},"*")
   * window.parent.postMessage({"tracker":"sp","action":"trackSelfDescribingEvent","data":{"schema":"iglu:com.snowplowanalytics.snowplow/link_click/jsonschema/1-0-1","data":{"targetUrl":"https://www.tijd.be","elementId":"link-id","elementClasses":["c-link"],"elementTarget":"","elementContent":"link text"}},"*")
   */
  function registerIframeListener() {
    logger.debug('postMessage tracking log on');
    const eventMethod = _window.addEventListener ? 'addEventListener' : 'attachEvent';
    const messageEvent = eventMethod === 'attachEvent' ? 'onmessage' : 'message';
    _window[eventMethod](messageEvent, function (event) {
      const trackingEvent = event.data;
      if (!trackingEvent.tracker) return;
      logger.debug('Tracking based on event', trackingEvent);
      if (trackingEvent.tracker === 'sp') {
        logger.debug('Sending to SP', trackingEvent);
        if (trackingEvent.action === 'trackPageView') {
          // TODO: remove track page views from multimedia tools
          //trackPageView(trackingEvent.customContext);
        } else if (trackingEvent.action === 'setUserId') {
          setUserId(trackingEvent.userId);
        } else if (trackingEvent.action === 'trackStructEvent') {
          const properties = trackingEvent.event;
          trackStructEvent({
            category: properties.category,
            action: properties.action,
            label: properties.label ? properties.label : '',
            property: properties.property ? properties.property : '',
            value: properties.value ? '' + properties.value : '',
            context: trackingEvent.customContext
          });
        } else if (trackingEvent.action === 'trackSelfDescribingEvent') {
          const selfDescEvent = trackingEvent.data;
          trackSelfDescribingEvent({
            event: {
              schema: selfDescEvent.schema,
              data: selfDescEvent.data
            },
            context: trackingEvent.customContext
          });
        } else {
          logger.warn('Event not mapped: ' + JSON.stringify(trackingEvent));
        }
      } else {
        logger.warn('Unknown tracking event: ' + JSON.stringify(trackingEvent));
      }
    });
  }

  function handleTrackerV2Call(method, ...args) {
    logger.debug('Called v2 tracker, forwarding to v3', 'function', method, 'args', args);
    if (isInitialised()) {
      if ('setUserId' === method) setUserId(args[0]);
      else if ('setCustomUrl' === method) setCustomUrl(args[0]);
      else if ('trackPageView' === method) trackPageView({title: args[0], context: args[1]});
      else if ('trackStructEvent' === method) {
        trackStructEvent({
          category: args[0],
          action: args[1],
          label: args[2] ? args[2] : '',
          property: args[3] ? args[3] : '',
          value: args[4] ? '' + args[4] : '',
          context: args[5]
        });
      } else if ('trackSelfDescribingEvent' === method) {
        const event = args[0];
        let contexts = args[1];
        trackSelfDescribingEvent({
          event: event,
          context: contexts
        });
      }
    } else {
      logger.debug("tracker not initialised, waiting");
      setTimeout(handleTrackerV2Call, _tracker_retryWaitTime, method, ...args);
    }
  }

  function addTrackingInfoToLinks(cssSelector, classToAdd, iteration, maxIterations) {
    // Just do this for nr of iterations, no matter if successfull or not. This is because loading these related items is async and no way to know if this async process has finished
    logger.debug("Looking for article links to tag", cssSelector, classToAdd, iteration, maxIterations);
    document.querySelectorAll(cssSelector).forEach(link => {
      link.classList.add(classToAdd);
    });
    if (iteration < maxIterations) {
      setTimeout(addTrackingInfoToLinks, 500, cssSelector, classToAdd, iteration + 1, maxIterations);
    }
  }

  function isInitialised() {
    return trackerInitialised === true;
  }

  //make this method available publicly, used in abo flow
  _window.createUserContext = createUserContext;
  //public method, used by Twipe
  _window.trmfn_public = {
    setDebug: setDebug,
    isInitialised: isInitialised,
    trackPageView: trackMediafinPageView,
    createUserContext: createUserContext,
    registerIframeListener: registerIframeListener,
    trackStructEvent: trackStructEvent,
    trackSelfDescribingEvent: trackSelfDescribingEvent,
    getSessionId: getSessionId
  }
  //backward compatibility with v2 tracker
  _window.trmfn = handleTrackerV2Call;

  //Add some additional tracking info to links that are in the document returned by the server
  addTrackingInfoToLinks('li.c-sectionreadmore__listitem a', 'track-article-readmore', 0, 10);
  addTrackingInfoToLinks('div.c-relatedarticleslist a', 'track-article-related', 0, 10);

  //init tracker and start tracking (fallback)
  const setupFallbackTimeoutId = setTimeout(()=>{
    logger.warn("No datalayer event received, firing the setup");
    setupSnowplow();
  }, _tracker_fallbackWaitTime);

  const currentValueDatalayer =  window[_datalayer_name];
  const _datalayer = new Datalayer();
  _datalayer.registerCallback(eventContainer => {
    logger.debug("Received datalayer event", eventContainer);
    if(eventContainer.event === 'paywall-ready'){
      clearTimeout(setupFallbackTimeoutId);
      paywallReadyEvent = eventContainer;
      setupSnowplow();
    }
  })
  window[_datalayer_name] = _datalayer
  if(currentValueDatalayer instanceof Array){
    currentValueDatalayer.forEach(item => _datalayer.push(item));
  }
}
